import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuthStore } from '@/store';
import {
  authService,
  cardService,
  listingService,
  userService,
  wishlistService,
  reviewService,
  forumService,
  collectionService,
  setWatchService,
  emailPreferencesService,
  paymentService,
  deckService,
  progressionService,
  moderationService,
  type EmailPreferences,
  type ReportEntityType,
  type ReportReason,
} from '@/lib/api';
import {
  User,
  Card,
  CardSearchFilters,
  PriceHistory,
  Listing,
  CreateListingData,
  LoginCredentials,
  RegisterData,
  PaginatedResponse,
} from '@/types';
import toast from 'react-hot-toast';

// Authentication Hooks
export const useLogin = () => {
  const setAuth = useAuthStore((state) => state.setAuth);

  return useMutation(
    (credentials: LoginCredentials) => authService.login(credentials),
    {
      onSuccess: (data) => {
        setAuth(data.user, data.tokens);
        toast.success('Welcome back!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Login failed');
      },
    }
  );
};

export const useRegister = () => {
  const setAuth = useAuthStore((state) => state.setAuth);

  return useMutation(
    (data: RegisterData) => authService.register(data),
    {
      onSuccess: (data) => {
        setAuth(data.user, data.tokens);
        toast.success('Account created successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Registration failed');
      },
    }
  );
};

export const useLogout = () => {
  const logout = useAuthStore((state) => state.logout);
  const tokens = useAuthStore((state) => state.tokens);

  return useMutation(
    () => authService.logout(tokens?.refreshToken || ''),
    {
      onSuccess: () => {
        logout();
        toast.success('Logged out successfully');
      },
    }
  );
};

export const useSendVerificationEmail = () =>
  useMutation(() => authService.sendVerificationEmail(), {
    onSuccess: () => toast.success('Verification email sent — check your inbox'),
    onError: () => toast.error('Failed to send verification email'),
  });

export const useVerifyEmail = () =>
  useMutation((token: string) => authService.verifyEmail(token), {
    onError: () => toast.error('Invalid or expired verification link'),
  });

export const useForgotPassword = () =>
  useMutation((email: string) => authService.forgotPassword(email), {
    onSuccess: () => toast.success('If that email exists, a reset link has been sent'),
    onError: () => toast.error('Failed to send reset email'),
  });

export const useResetPassword = () =>
  useMutation(({ token, password }: { token: string; password: string }) =>
    authService.resetPassword(token, password)
  );

export const useRequestDeleteAccount = () =>
  useMutation(() => authService.requestDeleteAccount(), {
    onSuccess: () => toast.success('Confirmation email sent — check your inbox'),
    onError: () => toast.error('Failed to send confirmation email'),
  });

export const useConfirmDeleteAccount = () =>
  useMutation((token: string) => authService.confirmDeleteAccount(token));

// Card Hooks
export const useCards = (filters: CardSearchFilters) => {
  return useQuery<PaginatedResponse<Card>, Error>(
    ['cards', filters],
    () => cardService.searchCards(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useCard = (id: string) => {
  return useQuery<Card, Error>(
    ['card', id],
    () => cardService.getCardById(id),
    {
      enabled: !!id,
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  );
};

export const usePriceHistory = (id: string, days: number = 30) => {
  return useQuery<PriceHistory[], Error>(
    ['priceHistory', id, days],
    () => cardService.getPriceHistory(id, days),
    {
      enabled: !!id,
      staleTime: 10 * 60 * 1000,
    }
  );
};

export const usePopularCards = (limit: number = 50) => {
  return useQuery<Card[], Error>(
    ['popularCards', limit],
    () => cardService.getPopularCards(limit),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
    }
  );
};

export const useGames = () => {
  return useQuery<string[], Error>(
    ['games'],
    () => cardService.getGames(),
    {
      staleTime: 60 * 60 * 1000, // 1 hour
    }
  );
};

export const useSets = (game: string) => {
  return useQuery<string[], Error>(
    ['sets', game],
    () => cardService.getSetsByGame(game),
    {
      enabled: !!game,
      staleTime: 60 * 60 * 1000,
    }
  );
};

// Listing Hooks
export const useListings = (filters?: any) => {
  const { enabled, ...queryFilters } = filters ?? {};
  return useQuery<PaginatedResponse<Listing>, Error>(
    ['listings', queryFilters],
    () => listingService.getListings(queryFilters),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 minutes
      enabled: enabled !== false,
    }
  );
};

export const useListing = (id: string) => {
  return useQuery<Listing, Error>(
    ['listing', id],
    () => listingService.getListingById(id),
    {
      enabled: !!id,
    }
  );
};

export const useCreateListing = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: CreateListingData) => listingService.createListing(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listings']);
        toast.success('Listing created successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to create listing');
      },
    }
  );
};

export const useUpdateListing = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: Partial<CreateListingData>) => listingService.updateListing(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listing', id]);
        queryClient.invalidateQueries(['listings']);
        toast.success('Listing updated successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to update listing');
      },
    }
  );
};

export const useDeleteListing = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => listingService.deleteListing(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listings']);
        queryClient.invalidateQueries(['userListings']);
        queryClient.invalidateQueries(['listing']);
        toast.success('Listing deleted successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to delete listing');
      },
    }
  );
};

export const useUnlistItem = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => listingService.updateListing(id, { status: 'cancelled' } as any),
    {
      onSuccess: (_data, id) => {
        queryClient.invalidateQueries(['listing', id]);
        queryClient.invalidateQueries(['listings']);
        queryClient.invalidateQueries(['userListings']);
        queryClient.invalidateQueries(['myCollection']);
        queryClient.invalidateQueries(['collection']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to remove listing');
      },
    }
  );
};

// User Hooks
export const useCurrentUser = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery<User, Error>(
    ['currentUser'],
    () => userService.getCurrentUser(),
    {
      enabled: isAuthenticated,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useUser = (id: string) => {
  return useQuery<User, Error>(
    ['user', id],
    () => userService.getUserById(id),
    {
      enabled: !!id,
      staleTime: 10 * 60 * 1000,
    }
  );
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const updateUser = useAuthStore((state) => state.updateUser);

  return useMutation(
    ({ id, data }: { id: string; data: Partial<User> }) => userService.updateUser(id, data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['currentUser']);
        queryClient.invalidateQueries(['user', data.id]);
        updateUser(data);
        toast.success('Profile updated successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to update profile');
      },
    }
  );
};

// Wishlist Hooks
export const useWishlist = (_filters?: { search?: string }) => {
  return useQuery(
    ['wishlist'],
    () => wishlistService.getItems(),
    { staleTime: 5 * 60 * 1000 }
  );
};

export const useWishlistItems = () => {
  return useQuery(
    ['wishlist'],
    () => wishlistService.getItems(),
    { staleTime: 5 * 60 * 1000 }
  );
};

export const useCreateWishlistItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (formData: FormData) => wishlistService.createItem(formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to add to wishlist');
      },
    }
  );
};

export const useQuickAddWishlist = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: { card_name: string; card_image_url?: string; set_name?: string; price_mode?: string; price_max?: number }) =>
      wishlistService.quickAdd(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist']);
        toast.success('Added to wishlist');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to add to wishlist');
      },
    }
  );
};

export const useUpdateWishlistItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: any }) => wishlistService.updateItem(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist']);
        toast.success('Wishlist item updated');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to update wishlist item');
      },
    }
  );
};

/** Permanently removes the wishlist item row. */
export const useDeleteWishlistItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => wishlistService.deleteItem(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist']);
        toast.success('Wishlist item deleted');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to delete wishlist item');
      },
    }
  );
};

/** Soft-deletes (marks as found). Reversible via useReactivateWishlistItem. */
export const useMarkWishlistFound = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => wishlistService.markAsFound(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist']);
        toast.success('Marked as found!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to update wishlist item');
      },
    }
  );
};

/** Re-activates a previously fulfilled wishlist item. */
export const useReactivateWishlistItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => wishlistService.reactivateItem(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist']);
        toast.success('Wishlist item reactivated');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to reactivate wishlist item');
      },
    }
  );
};

// Legacy aliases
export const useAddToWishlist = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (cardId: string) => wishlistService.addToWishlist(cardId),
    {
      onSuccess: () => queryClient.invalidateQueries(['wishlist']),
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to add to wishlist');
      },
    }
  );
};

export const useRemoveFromWishlist = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => wishlistService.deleteItem(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist']);
        toast.success('Removed from wishlist');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to remove from wishlist');
      },
    }
  );
};
// Bid & Offer Hooks
export const usePlaceBid = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { listing_id: string; amount: number }>(
    ({ listing_id, amount }: { listing_id: string; amount: number }) =>
      listingService.placeBid(listing_id, amount),
    {
      onSuccess: (_, { listing_id }) => {
        queryClient.invalidateQueries(['listing', listing_id]);
        toast.success('Bid placed successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to place bid');
      },
    }
  );
};

export const useMakeOffer = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { listing_id: string; amount: number; message?: string }>(
    ({ listing_id, amount, message }: { listing_id: string; amount: number; message?: string }) =>
      listingService.makeOffer(listing_id, amount, message),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listings']);
        toast.success('Offer sent!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to send offer');
      },
    }
  );
};

/** Fetches listings that match the current user's active wishlist items. */
export const useWishlistListings = (options?: { enabled?: boolean }) => {
  return useQuery(
    ['wishlistListings'],
    () => listingService.getWishlistListings(),
    {
      enabled: options?.enabled !== false,
      staleTime: 60 * 1000, // 1 minute — fresh enough for explore
    }
  );
};

export const useCardListings = (cardId: string) => {
  return useQuery<Listing[], Error>(
    ['cardListings', cardId],
    () => listingService.getCardListings(cardId),
    {
      enabled: !!cardId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

export const useSearchCards = (query: string) => {
  return useQuery<Card[], Error>(
    ['searchCards', query],
    () => cardService.searchCardsByName(query),
    {
      enabled: query.length > 1,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useSearchUsers = (query: string) => {
  return useQuery<User[], Error>(
    ['searchUsers', query],
    () => userService.searchUsers(query),
    {
      enabled: query.length > 1,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useUserReviews = (userId: string) => {
  return useQuery(
    ['userReviews', userId],
    () => reviewService.getReviewsForUser(userId),
    {
      enabled: !!userId,
      staleTime: 2 * 60 * 1000,
    }
  );
};

export const useSubmitReview = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: { rating: number; comment?: string; review_type?: string }) =>
      reviewService.submitReview(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userReviews', userId]);
        queryClient.invalidateQueries(['user', userId]);
        queryClient.invalidateQueries(['profile', userId]);
        toast.success('Review submitted!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to submit review');
      },
    }
  );
};

export const useMyCollection = (filters?: { search?: string; sort?: string }) => {
  return useQuery(
    ['myCollection', filters],
    () => collectionService.getMyCollection(filters),
    {
      staleTime: 2 * 60 * 1000,
    }
  );
};
// Profile Hooks
export const useProfile = (id?: string) => {
  return useQuery<User, Error>(
    ['profile', id],
    () => id ? userService.getUserById(id) : userService.getCurrentUser(),
    {
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useUploadAvatar = () => {
  const updateUser = useAuthStore((state) => state.updateUser);
  const queryClient = useQueryClient();

  return useMutation(
    (file: File) => userService.uploadAvatar(file),
    {
      onSuccess: (data) => {
        updateUser({ avatar_url: data.avatar_url });
        queryClient.invalidateQueries(['currentUser']);
        queryClient.invalidateQueries(['profile']);
        toast.success('Profile picture updated!');
      },
      onError: () => {
        toast.error('Failed to upload profile picture');
      },
    }
  );
};

export const useDeleteAvatar = () => {
  const updateUser = useAuthStore((state) => state.updateUser);
  const queryClient = useQueryClient();

  return useMutation(
    () => userService.deleteAvatar(),
    {
      onSuccess: () => {
        updateUser({ avatar_url: undefined });
        queryClient.invalidateQueries(['currentUser']);
        queryClient.invalidateQueries(['profile']);
        toast.success('Profile picture removed');
      },
      onError: () => {
        toast.error('Failed to remove profile picture');
      },
    }
  );
};

export const useUpdateProfile = () => {
  const updateUser = useAuthStore((state) => state.updateUser);
  const queryClient = useQueryClient();

  return useMutation(
    (data: { first_name?: string; last_name?: string; bio?: string }) =>
      userService.updateProfile(data),
    {
      onSuccess: (data) => {
        updateUser(data);
        queryClient.invalidateQueries(['currentUser']);
        queryClient.invalidateQueries(['profile']);
        toast.success('Profile updated!');
      },
      onError: () => {
        toast.error('Failed to update profile');
      },
    }
  );
};

export const useUserListings = (userId?: string) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // When no userId is given the API hits /users/me/listings which requires auth
  return useQuery<Listing[], Error>(
    ['userListings', userId],
    () => listingService.getUserListings(userId || ''),
    {
      enabled: !!userId || isAuthenticated,
      staleTime: 2 * 60 * 1000,
    }
  );
};

// Forum Hooks
export const useForumCategories = () =>
  useQuery(['forumCategories'], () => forumService.getCategories(), { staleTime: 2 * 60 * 1000 });

export const useForumCategoryThreads = (slug: string, page = 1) =>
  useQuery(['forumThreads', slug, page], () => forumService.getCategoryThreads(slug, page), {
    enabled: !!slug,
    keepPreviousData: true,
    staleTime: 30 * 1000,
  });

export const useForumThread = (id: string, page = 1) =>
  useQuery(['forumThread', id, page], () => forumService.getThread(id, page), {
    enabled: !!id,
    keepPreviousData: true,
    staleTime: 30 * 1000,
  });

export const useCreateForumThread = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { category_id: string; title: string; body: string; poll?: any }) =>
      forumService.createThread(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['forumCategories']);
        queryClient.invalidateQueries(['forumThreads']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to create thread');
      },
    }
  );
};

export const useCreateForumPost = (threadId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (body: string) => forumService.createPost(threadId, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['forumThread', threadId]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to post reply');
      },
    }
  );
};

export const useEditForumPost = (threadId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ postId, body }: { postId: string; body: string }) => forumService.editPost(postId, body),
    {
      onSuccess: () => queryClient.invalidateQueries(['forumThread', threadId]),
    }
  );
};

export const useReactToPost = (threadId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ postId, reaction }: { postId: string; reaction: string }) =>
      forumService.reactToPost(postId, reaction),
    {
      onSuccess: () => queryClient.invalidateQueries(['forumThread', threadId]),
    }
  );
};

export const useVotePoll = (threadId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ pollId, optionId }: { pollId: string; optionId: string }) =>
      forumService.votePoll(pollId, optionId),
    {
      onSuccess: () => queryClient.invalidateQueries(['forumThread', threadId]),
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Already voted');
      },
    }
  );
};

export const useForumSearch = (q: string, page = 1) =>
  useQuery(['forumSearch', q, page], () => forumService.searchThreads(q, page), {
    enabled: q.length > 1,
    staleTime: 30 * 1000,
  });

export const useForumUserPosts = (userId: string, limit = 5) =>
  useQuery(['forumUserPosts', userId, limit], () => forumService.getUserPosts(userId, limit), {
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  });

export const useDeleteForumPost = (threadId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (postId: string) => forumService.deletePost(postId),
    {
      onSuccess: () => queryClient.invalidateQueries(['forumThread', threadId]),
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to delete post');
      },
    }
  );
};

export const useDeleteForumThread = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (threadId: string) => forumService.deleteThread(threadId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['forumCategories']);
        queryClient.invalidateQueries(['forumThreads']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to delete thread');
      },
    }
  );
};

export const usePinForumThread = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ threadId, pinned }: { threadId: string; pinned: boolean }) =>
      forumService.pinThread(threadId, pinned),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['forumThreads']);
        queryClient.invalidateQueries(['forumThread']);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to update pin');
      },
    }
  );
};

// ============================================================
// Collection Hooks
// ============================================================

export const useCollection = (filters?: {
  search?: string;
  sort?: string;
  game?: string;
  set_name?: string;
  condition?: string;
  status?: 'for_sale' | 'not_listed' | 'sold' | 'for_trade';
  item_category?: 'single_card' | 'bulk_merch';
  page?: number;
  limit?: number;
  enabled?: boolean;
}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { enabled = true, ...queryFilters } = filters ?? {};
  return useQuery(
    ['collection', queryFilters],
    () => collectionService.getMyCollection(queryFilters),
    { staleTime: 2 * 60 * 1000, keepPreviousData: true, enabled: enabled && isAuthenticated }
  );
}

export const useCollectionSetCompletion = (game?: string) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return useQuery(
    ['collectionSets', game],
    () => collectionService.getMySetCompletion(game),
    { staleTime: 5 * 60 * 1000, enabled: isAuthenticated }
  );
};

export const useCollectionFilters = () =>
  useQuery(
    ['collectionFilters'],
    () => collectionService.getMyFilters(),
    { staleTime: 5 * 60 * 1000 }
  );

export const useCardCollectionStatus = (cardId: string) =>
  useQuery(
    ['cardCollectionStatus', cardId],
    () => collectionService.getCardStatus(cardId),
    { enabled: !!cardId, staleTime: 60 * 1000 }
  );

export const useUserCollection = (userId: string, page = 1) =>
  useQuery(
    ['userCollection', userId, page],
    () => collectionService.getUserCollection(userId, page),
    { enabled: !!userId, staleTime: 2 * 60 * 1000, keepPreviousData: true }
  );

export const useAddToCollection = () => {
  const qc = useQueryClient();
  return useMutation(
    (data: {
      card_id?: string;
      item_category?: string;
      item_name?: string;
      item_brand?: string;
      condition: string;
      quantity?: number;
      acquisition_price?: number | null;
      notes?: string | null;
      is_for_sale?: boolean;
      is_for_trade?: boolean;
    }) => collectionService.addCard(data),
    {
      onSuccess: () => {
        qc.invalidateQueries('collection');
        qc.invalidateQueries('myCollection');
        qc.invalidateQueries('collectionFilters');
        qc.invalidateQueries('collectionSets');
        qc.invalidateQueries('cardCollectionStatus');
        qc.invalidateQueries('currentUser');
        qc.invalidateQueries('missingCards');
      },
    }
  );
};

export const useUpdateCollectionItem = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: { quantity?: number; condition?: string; notes?: string | null; is_for_sale?: boolean; is_for_trade?: boolean; make_an_offer?: boolean } }) =>
      collectionService.updateItem(id, data),
    {
      onSuccess: () => {
        qc.invalidateQueries('collection');
        qc.invalidateQueries('myCollection');
        qc.invalidateQueries('collectionSets');
      },
    }
  );
};

export const useRemoveFromCollection = () => {
  const qc = useQueryClient();
  return useMutation(
    (id: string) => collectionService.removeItem(id),
    {
      onSuccess: () => {
        qc.invalidateQueries('collection');
        qc.invalidateQueries('myCollection');
        qc.invalidateQueries('collectionFilters');
        qc.invalidateQueries('collectionSets');
        qc.invalidateQueries('cardCollectionStatus');
        qc.invalidateQueries('currentUser');
        qc.invalidateQueries('missingCards');
      },
    }
  );
};

export const useReorderCollection = () => {
  const qc = useQueryClient();
  return useMutation(
    (items: { id: string; sort_order: number }[]) => collectionService.reorder(items),
    {
      onSuccess: () => {
        qc.invalidateQueries('collection');
        qc.invalidateQueries('myCollection');
      },
    }
  );
};

export const useQuickList = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ id, data }: {
      id: string;
      data: {
        price: number;
        listing_type?: string;
        quantity?: number;
        shipping_price?: number;
        description?: string;
      };
    }) => collectionService.quickList(id, data),
    {
      onSuccess: () => {
        qc.invalidateQueries('myCollection');
        qc.invalidateQueries('collection');
        qc.invalidateQueries('myListings');
      },
    }
  );
};

export const useFeaturedCollection = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return useQuery(['featuredCollection'], () => collectionService.getFeatured(), {
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
};

export const useCollectionShowcase = (limit = 20) =>
  useQuery(['collectionShowcase', limit], () => collectionService.getShowcase(limit), {
    staleTime: 5 * 60 * 1000,
  });

export const usePublicVault = (userId: string | undefined) =>
  useQuery(
    ['publicVault', userId],
    () => collectionService.getPublicVault(userId!),
    { enabled: !!userId, staleTime: 60_000 }
  );

export const useFeatureCollectionItem = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ id, featured }: { id: string; featured: boolean }) =>
      collectionService.featureItem(id, featured),
    {
      onSuccess: () => {
        qc.invalidateQueries('featuredCollection');
        qc.invalidateQueries('collection');
        qc.invalidateQueries('myCollection');
      },
    }
  );
};

export const useFindCardListings = (cardId: string | undefined, excludeUserId?: string) => {
  return useQuery(
    ['cardListings', cardId, excludeUserId],
    () => collectionService.findCardListings(cardId!, excludeUserId),
    { enabled: !!cardId }
  );
};

export const useMissingCards = (game: string | null, set_name: string | null) =>
  useQuery(
    ['missingCards', game, set_name],
    () => collectionService.getMissingCards(game!, set_name!),
    { enabled: !!game && !!set_name, staleTime: 60_000 }
  );

export const useSetWatches = () =>
  useQuery(['setWatches'], () => setWatchService.getAll(), { staleTime: 60_000 });

export const useToggleSetWatch = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ game, set_name }: { game: string; set_name: string }) =>
      setWatchService.toggle(game, set_name),
    {
      onSuccess: () => {
        qc.invalidateQueries('setWatches');
      },
    }
  );
};

export const useSetCards = (game: string | null, set_name: string | null) =>
  useQuery(
    ['setCards', game, set_name],
    () => collectionService.getSetCards(game!, set_name!),
    { enabled: !!game && !!set_name, staleTime: 60_000 }
  );

export const useImportSetCards = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ game, set_name }: { game: string; set_name: string }) =>
      cardService.importSetCards(game, set_name),
    {
      onSuccess: (_data, { game, set_name }) => {
        qc.invalidateQueries(['setCards', game, set_name]);
        qc.invalidateQueries(['collectionSets']);
      },
    }
  );
};

export const useEmailPreferences = () =>
  useQuery(['emailPreferences'], () => emailPreferencesService.get(), {
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateEmailPreferences = () => {
  const qc = useQueryClient();
  return useMutation(
    (prefs: Partial<EmailPreferences>) => emailPreferencesService.update(prefs),
    {
      onSuccess: (data) => {
        qc.setQueryData(['emailPreferences'], data);
        toast.success('Email preferences saved');
      },
      onError: () => toast.error('Failed to save preferences'),
    }
  );
};

// ── Payment / Stripe hooks ───────────────────────────────────────────────────

export const useCreateCheckout = () =>
  useMutation((listingId: string) => paymentService.createCheckout(listingId));

export const useConnectStatus = () =>
  useQuery(['stripeConnectStatus'], () => paymentService.getConnectStatus(), {
    staleTime: 60 * 1000,
    retry: false,
  });

export const useOnboardConnect = () => {
  const qc = useQueryClient();
  return useMutation(() => paymentService.onboardConnect(), {
    onSuccess: () => qc.invalidateQueries(['stripeConnectStatus']),
  });
};

export const useOrders = () =>
  useQuery(['orders'], () => paymentService.getOrders(), { staleTime: 30 * 1000 });

export const useOrder = (id: string) =>
  useQuery(['order', id], () => paymentService.getOrder(id), {
    enabled: !!id,
    refetchInterval: (data: any) =>
      data?.status === 'pending' ? 3000 : false, // poll while pending
  });

export const useAddTracking = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ orderId, trackingNumber }: { orderId: string; trackingNumber: string }) =>
      paymentService.addTracking(orderId, trackingNumber),
    { onSuccess: (_d, { orderId }) => qc.invalidateQueries(['order', orderId]) }
  );
};

export const useMarkDelivered = () => {
  const qc = useQueryClient();
  return useMutation(
    (orderId: string) => paymentService.markDelivered(orderId),
    { onSuccess: (_d, orderId) => qc.invalidateQueries(['order', orderId]) }
  );
};

// ── Deck Builder hooks ───────────────────────────────────────────────────────

export const useDecks = (filters?: { game?: string; format?: string }) =>
  useQuery(['decks', filters], () => deckService.getDecks(filters), { staleTime: 30_000 });

export const useDeck = (deckId: string) =>
  useQuery(['deck', deckId], () => deckService.getDeck(deckId), { enabled: !!deckId });

export const useDeckStats = (deckId: string) =>
  useQuery(['deckStats', deckId], () => deckService.getStats(deckId), { enabled: !!deckId });

export const useDeckMissingCards = (deckId: string) =>
  useQuery(['deckMissingCards', deckId], () => deckService.getMissingCards(deckId), { enabled: !!deckId });

export const useDeckTemplates = (filters?: { game?: string; tier?: string }) =>
  useQuery(['deckTemplates', filters], () => deckService.getTemplates(filters), { staleTime: 5 * 60_000 });

export const useCreateDeck = () => {
  const qc = useQueryClient();
  return useMutation((data: Parameters<typeof deckService.createDeck>[0]) => deckService.createDeck(data), {
    onSuccess: () => { qc.invalidateQueries(['decks']); toast.success('Deck created!'); },
  });
};

export const useUpdateDeck = (deckId: string) => {
  const qc = useQueryClient();
  return useMutation((data: Parameters<typeof deckService.updateDeck>[1]) => deckService.updateDeck(deckId, data), {
    onSuccess: () => { qc.invalidateQueries(['deck', deckId]); qc.invalidateQueries(['decks']); },
  });
};

export const useDeleteDeck = () => {
  const qc = useQueryClient();
  return useMutation((id: string) => deckService.deleteDeck(id), {
    onSuccess: () => { qc.invalidateQueries(['decks']); toast.success('Deck deleted'); },
  });
};

export const useAddCardToDeck = (deckId: string) => {
  const qc = useQueryClient();
  return useMutation(
    (data: { card_id: string; quantity: number; category?: string }) => deckService.addCard(deckId, data),
    { onSuccess: () => { qc.invalidateQueries(['deck', deckId]); qc.invalidateQueries(['deckStats', deckId]); } }
  );
};

export const useUpdateDeckCard = (deckId: string) => {
  const qc = useQueryClient();
  return useMutation(
    ({ cardId, quantity, category }: { cardId: string; quantity: number; category?: string }) =>
      deckService.updateCard(deckId, cardId, { quantity, category }),
    { onSuccess: () => { qc.invalidateQueries(['deck', deckId]); qc.invalidateQueries(['deckStats', deckId]); } }
  );
};

export const useRemoveCardFromDeck = (deckId: string) => {
  const qc = useQueryClient();
  return useMutation(
    ({ cardId, category }: { cardId: string; category?: string }) => deckService.removeCard(deckId, cardId, category),
    { onSuccess: () => { qc.invalidateQueries(['deck', deckId]); qc.invalidateQueries(['deckStats', deckId]); } }
  );
};

export const useBatchAddCards = (deckId: string) => {
  const qc = useQueryClient();
  return useMutation(
    (cards: { card_id: string; quantity: number; category?: string }[]) =>
      deckService.batchAddCards(deckId, cards),
    { onSuccess: () => { qc.invalidateQueries(['deck', deckId]); qc.invalidateQueries(['deckStats', deckId]); } }
  );
};

export const useClearDeckCards = (deckId: string) => {
  const qc = useQueryClient();
  return useMutation(
    () => deckService.clearCards(deckId),
    { onSuccess: () => { qc.invalidateQueries(['deck', deckId]); qc.invalidateQueries(['deckStats', deckId]); } }
  );
};

export const usePopulateDeck = (deckId: string) => {
  const qc = useQueryClient();
  return useMutation(
    (data: { query: string; limit?: number; category?: string }) =>
      deckService.populateDeck(deckId, data),
    {
      onSuccess: () => {
        qc.invalidateQueries(['deck', deckId]);
        qc.invalidateQueries(['deckStats', deckId]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Failed to populate deck');
      },
    }
  );
};

export const useAddCardByNameToDeck = (deckId: string) => {
  const qc = useQueryClient();
  return useMutation(
    (data: { name: string; quantity?: number; category?: string }) =>
      deckService.addCardByName(deckId, data),
    {
      onSuccess: () => {
        qc.invalidateQueries(['deck', deckId]);
        qc.invalidateQueries(['deckStats', deckId]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Card not found — try a different name');
      },
    }
  );
};

export const useCreateDeckFromTemplate = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ templateId, name }: { templateId: string; name?: string }) => deckService.createFromTemplate(templateId, name),
    { onSuccess: () => { qc.invalidateQueries(['decks']); toast.success('Deck created from template!'); } }
  );
};
// ── Progression Hooks ──────────────────────────────────────────────────────────

export const useUserLevel = (userId: string | undefined) =>
  useQuery(
    ['userLevel', userId],
    () => progressionService.getUserLevel(userId!),
    { enabled: !!userId, staleTime: 60_000 }
  );

export const useXpHistory = (userId: string | undefined, limit = 20) =>
  useQuery(
    ['xpHistory', userId, limit],
    () => progressionService.getXpHistory(userId!, limit),
    { enabled: !!userId, staleTime: 30_000 }
  );

export const useAllTrophies = () =>
  useQuery('trophyDefinitions', () => progressionService.getAllTrophies(), { staleTime: 300_000 });

export const useUserTrophies = (userId: string | undefined) =>
  useQuery(
    ['userTrophies', userId],
    () => progressionService.getUserTrophies(userId!),
    { enabled: !!userId, staleTime: 60_000 }
  );

export const useUserTrophyProgress = (userId: string | undefined) =>
  useQuery(
    ['userTrophyProgress', userId],
    () => progressionService.getUserTrophyProgress(userId!),
    { enabled: !!userId, staleTime: 60_000 }
  );

export const useUserReputation = (userId: string | undefined) =>
  useQuery(
    ['userReputation', userId],
    () => progressionService.getUserReputation(userId!),
    { enabled: !!userId, staleTime: 60_000 }
  );

export const useLeaderboard = (type: 'levels' | 'reputation' | 'trophies', limit = 50) =>
  useQuery(
    ['leaderboard', type, limit],
    () => progressionService.getLeaderboard(type, limit),
    { staleTime: 120_000 }
  );

export const useAwardXp = () => {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useMutation(
    ({ action, description, relatedEntityType, relatedEntityId }: {
      action: string;
      description?: string;
      relatedEntityType?: string;
      relatedEntityId?: string;
    }) => progressionService.awardXp(action, description, relatedEntityType, relatedEntityId),
    {
      onSuccess: () => {
        if (user?.id) {
          qc.invalidateQueries(['userLevel', user.id]);
          qc.invalidateQueries(['xpHistory', user.id]);
        }
      },
    }
  );
};

export const useCheckTrophies = () => {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useMutation(
    (requirementTypes: string[]) => progressionService.checkTrophies(requirementTypes),
    {
      onSuccess: () => {
        if (user?.id) {
          qc.invalidateQueries(['userTrophies', user.id]);
          qc.invalidateQueries(['userTrophyProgress', user.id]);
        }
      },
    }
  );
};

export const useToggleTrophyDisplay = (userId: string) => {
  const qc = useQueryClient();
  return useMutation(
    ({ trophyId, isDisplayed }: { trophyId: string; isDisplayed: boolean }) =>
      progressionService.toggleTrophyDisplay(userId, trophyId, isDisplayed),
    {
      onSuccess: () => {
        qc.invalidateQueries(['userTrophies', userId]);
        qc.invalidateQueries(['userTrophyProgress', userId]);
      },
    }
  );
};

// ── Moderation Hooks ──────────────────────────────────────────────────────────

export const useReportContent = () =>
  useMutation(
    (data: {
      reported_entity_type: ReportEntityType;
      reported_entity_id: string;
      reported_user_id: string;
      reason: ReportReason;
      description?: string;
    }) => moderationService.submitReport(data),
    {
      onError: (err: any) => {
        toast.error(err.response?.data?.error?.message || 'Failed to submit report');
      },
    }
  );

export const useBlockUser = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ userId, reason }: { userId: string; reason?: string }) =>
      moderationService.blockUser(userId, reason),
    {
      onSuccess: () => {
        qc.invalidateQueries(['blocked-users']);
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error?.message || 'Failed to block user');
      },
    }
  );
};

export const useUnblockUser = () => {
  const qc = useQueryClient();
  return useMutation(
    (userId: string) => moderationService.unblockUser(userId),
    {
      onSuccess: () => {
        qc.invalidateQueries(['blocked-users']);
        toast.success('User unblocked');
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error?.message || 'Failed to unblock user');
      },
    }
  );
};

export const useBlockedUsers = () =>
  useQuery(['blocked-users'], () => moderationService.getBlockedUsers());

export const useAdminBanUser = () => {
  const qc = useQueryClient();
  return useMutation(
    ({ userId, reason, duration }: { userId: string; reason: string; duration?: number }) =>
      moderationService.banUser(userId, reason, duration),
    {
      onSuccess: () => {
        toast.success('User banned');
        qc.invalidateQueries(['admin-reports']);
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error?.message || 'Failed to ban user');
      },
    }
  );
};

export const useAdminUnbanUser = () => {
  const qc = useQueryClient();
  return useMutation(
    (userId: string) => moderationService.unbanUser(userId),
    {
      onSuccess: () => {
        toast.success('User unbanned');
        qc.invalidateQueries(['admin-reports']);
      },
    }
  );
};
